/* PCIS — Status Mapping (route /supply/pcis-health/status-mapping).
 *
 * Ops-managed mapping of a courier's raw status string → the PCIS/ProShip status code that PCIS
 * forwards to order_history. Admins pick a courier, see its current mappings, and add / edit / delete
 * them directly, or bulk-load via Excel (download template → fill → upload).
 *
 * ProShip is the authority for the status code (from OrderStatusEnum) — both the direct-edit dropdown
 * and the Excel template's in-cell dropdown are sourced from it, so a code can never be hand-typed.
 * PCIS stores what we send and audits every change (who = the logged-in user, forwarded server-side).
 *
 * All calls go to ProShip /api/pcis-admin/status-* (which proxies PCIS); the browser never sees PCIS's
 * admin key. GET list is camelCase (the entity); the upsert body is snake_case (the PCIS request DTO).
 */
import React, { useMemo, useState } from 'react'
import { Box, Stack, Button, Typography, IconButton, Tooltip, Chip, Alert, TextField, ToggleButton, ToggleButtonGroup, CircularProgress } from '@mui/material'
import AddOutlinedIcon from '@mui/icons-material/AddOutlined'
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined'
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined'
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined'
import PageTitle from '../../../components/PageTitle'
import { PageHeader, SearchSelect, Modal, FileDrop, ConfirmDialog, TableSkeleton, LoadErrorPanel, useToast, exportTable } from '../components'
import ProzoDataGrid from '../../../common/ProzoDataGrid'
import { get } from '../../../common/constants'
import { downloadExcelFile } from '../../../common/util'
import {
  usePcisStatusCouriersQuery,
  usePcisStatusCodesQuery,
  usePcisShipmentTypesQuery,
  usePcisStatusMappingsQuery,
  useUpsertPcisStatusMappingMutation,
  useDeletePcisStatusMappingMutation,
  useUploadPcisStatusMappingsMutation,
} from '../../../redux/supplyApiSlice'

const OTP_OPTIONS = [
  { value: '', label: '— none —' },
  { value: 'DELIVERY', label: 'DELIVERY' },
  { value: 'CANCELLATION', label: 'CANCELLATION' },
]
const EMPTY_FORM = { id: null, courier_code: '', courier_status: '', pcis_status_code: '', otp_stage: '', shipment_type: '' }

export default function PcisStatusMapping() {
  const toast = useToast()

  const [courier, setCourier] = useState('')
  const [page, setPage] = useState(0)
  const [sizePerPage, setSizePerPage] = useState(15)

  const [form, setForm] = useState(null)          // null = closed; object = add/edit dialog open
  const [deleteTarget, setDeleteTarget] = useState(null)
  const [uploadOpen, setUploadOpen] = useState(false)
  const [uploadFile, setUploadFile] = useState(null)
  const [uploadResult, setUploadResult] = useState(null)
  const [downloading, setDownloading] = useState(false)

  // ── Unmapped-status download ─────────────────────────────────────────
  const [unmappedDateMode, setUnmappedDateMode] = useState('today')
  const [unmappedCustomDate, setUnmappedCustomDate] = useState('')
  const [unmappedDownloading, setUnmappedDownloading] = useState(false)

  function getUnmappedDate() {
    if (unmappedDateMode === 'today') {
      return new Date().toISOString().slice(0, 10)
    }
    if (unmappedDateMode === 'yesterday') {
      const d = new Date()
      d.setDate(d.getDate() - 1)
      return d.toISOString().slice(0, 10)
    }
    return unmappedCustomDate || ''
  }

  async function downloadUnmappedStatuses() {
    const date = getUnmappedDate()
    if (!date) return toast('Select a date first.', 'error')
    setUnmappedDownloading(true)
    try {
      const res = await get('pcis-admin/unmapped-status/download', { date }, { responseType: 'blob' })
      downloadExcelFile(res.data, `unmapped_statuses_${date}`)
      toast(`Downloaded unmapped statuses for ${date}.`, 'success')
    } catch (e) {
      toast('Could not download unmapped statuses.', 'error')
    }
    setUnmappedDownloading(false)
  }

  const { data: couriers = [], isLoading: couriersLoading } = usePcisStatusCouriersQuery()
  const { data: statusCodes = [] } = usePcisStatusCodesQuery()
  const { data: shipmentTypes = [] } = usePcisShipmentTypesQuery()
  const {
    data: mappings = [], isFetching, isError, refetch,
  } = usePcisStatusMappingsQuery(courier, { skip: !courier })

  const [upsert, { isLoading: saving }] = useUpsertPcisStatusMappingMutation()
  const [remove, { isLoading: deleting }] = useDeletePcisStatusMappingMutation()
  const [upload, { isLoading: uploading }] = useUploadPcisStatusMappingsMutation()

  const statusOptions = useMemo(
    () => statusCodes.map((s) => ({ value: s.code, label: `${s.code} — ${s.description}` })),
    [statusCodes],
  )

  const shipmentOptions = useMemo(() => {
    const types = shipmentTypes.length ? shipmentTypes : ['FORWARD', 'REVERSE']
    return [{ value: '', label: '— none —' }, ...types.map((t) => ({ value: t, label: t }))]
  }, [shipmentTypes])

  const columns = [
    { field: 'courierStatus', headerName: 'Courier Status', flex: 1.4, minWidth: 200 },
    {
      field: 'pcisStatusCode', headerName: 'PCIS Status', flex: 1.6, minWidth: 240,
      renderCell: (p) => (
        <span><b>{p.row.pcisStatusCode}</b>{p.row.pcisStatusDescription ? ` — ${p.row.pcisStatusDescription}` : ''}</span>
      ),
    },
    {
      field: 'shipmentType', headerName: 'Shipment Type', width: 150,
      renderCell: (p) => (p.row.shipmentType ? <Chip size='small' variant='outlined' label={p.row.shipmentType} /> : <span style={{ opacity: 0.5 }}>—</span>),
    },
    {
      field: 'otpStage', headerName: 'OTP Stage', width: 150,
      renderCell: (p) => (p.row.otpStage ? <Chip size='small' label={p.row.otpStage} /> : <span style={{ opacity: 0.5 }}>—</span>),
    },
    {
      field: 'actions', headerName: 'Actions', width: 120, sortable: false, filterable: false,
      renderCell: (p) => (
        <Stack direction='row' spacing={0.5}>
          <Tooltip title='Edit'>
            <IconButton size='small' onClick={() => openEdit(p.row)}><EditOutlinedIcon fontSize='small' /></IconButton>
          </Tooltip>
          <Tooltip title='Delete'>
            <IconButton size='small' color='error' onClick={() => setDeleteTarget(p.row)}><DeleteOutlineOutlinedIcon fontSize='small' /></IconButton>
          </Tooltip>
        </Stack>
      ),
    },
  ]

  function openAdd() {
    setForm({ ...EMPTY_FORM, courier_code: courier || '' })
  }
  function openEdit(row) {
    setForm({
      id: row.id,
      courier_code: row.courierCode,
      courier_status: row.courierStatus,
      pcis_status_code: row.pcisStatusCode,
      otp_stage: row.otpStage || '',
      shipment_type: row.shipmentType || '',
    })
  }

  async function saveForm() {
    if (!form.courier_code) return toast('Pick a courier.', 'error')
    if (!form.courier_status || !String(form.courier_status).trim()) return toast('Courier status is required.', 'error')
    if (form.pcis_status_code === '' || form.pcis_status_code === null || form.pcis_status_code === undefined) {
      return toast('Pick a PCIS status.', 'error')
    }
    const body = {
      courier_code: form.courier_code,
      courier_status: String(form.courier_status).trim(),
      pcis_status_code: Number(form.pcis_status_code),
      otp_stage: form.otp_stage || null,
      shipment_type: form.shipment_type || null,
    }
    try {
      await upsert(body).unwrap()
      toast(`Mapping saved for ${form.courier_code}.`, 'success')
      setForm(null)
      if (form.courier_code === courier) refetch()
      else setCourier(form.courier_code)   // jump to the courier we just edited
    } catch (e) {
      toast(e?.message || 'Could not save mapping.', 'error')
    }
  }

  async function confirmDelete() {
    try {
      await remove({ id: deleteTarget.id, courierCode: deleteTarget.courierCode }).unwrap()
      toast('Mapping deleted.', 'success')
      setDeleteTarget(null)
      refetch()
    } catch (e) {
      toast(e?.message || 'Could not delete mapping.', 'error')
      setDeleteTarget(null)
    }
  }

  // Download the selected courier's CURRENT mappings as .xlsx (distinct from
  // "Template", which is the blank upload format served by the backend).
  function downloadMappings() {
    if (!courier || !mappings.length) return toast('Select a courier with mappings first.', 'error')
    exportTable(mappings, [
      { label: 'Courier', key: 'courierCode' },
      { label: 'Courier Status', key: 'courierStatus' },
      { label: 'PCIS Status Code', key: 'pcisStatusCode' },
      { label: 'PCIS Status Description', key: 'pcisStatusDescription' },
      { label: 'OTP Stage', value: (r) => r.otpStage || '' },
    ], `${courier}_status_mappings`)
    toast(`Downloaded ${mappings.length} mapping(s) for ${courier}.`, 'success')
  }

  async function downloadTemplate() {
    setDownloading(true)
    try {
      const res = await get('pcis-admin/status-mappings/sample', {}, { responseType: 'blob' })
      downloadExcelFile(res.data, 'status_mappings_template')
    } catch (e) {
      toast('Could not download the template.', 'error')
    }
    setDownloading(false)
  }

  // Download the SELECTED courier's current mappings in the upload format (edit → re-upload).
  async function downloadMappings() {
    if (!courier) return toast('Select a courier first.', 'error')
    setDownloading(true)
    try {
      const res = await get('pcis-admin/status-mappings/export', { courier_code: courier }, { responseType: 'blob' })
      downloadExcelFile(res.data, `status_mappings_${courier}`)
    } catch (e) {
      toast('Could not download the mappings.', 'error')
    }
    setDownloading(false)
  }

  async function doUpload() {
    if (!uploadFile) return toast('Choose an .xlsx file first.', 'error')
    try {
      const result = await upload({ file: uploadFile }).unwrap()
      setUploadResult(result)
      const failed = (result && result.validation_errors && result.validation_errors.length) || 0
      toast(`Uploaded: ${result?.sent_to_pcis ?? 0} row(s) sent${failed ? `, ${failed} rejected` : ''}.`,
        failed ? 'warning' : 'success')
      if (courier) refetch()
    } catch (e) {
      toast(e?.message || 'Upload failed.', 'error')
    }
  }

  function closeUpload() {
    setUploadOpen(false)
    setUploadFile(null)
    setUploadResult(null)
  }

  return (
    <>
      <PageTitle title='PCIS · Status Mapping' />
      <PageHeader
        title='Status Mapping'
        sub='Map each courier status to the PCIS status PCIS forwards to order history. Edit directly or bulk-upload via Excel.'
        actions={
          <>
            <Button size='small' variant='outlined' startIcon={<DownloadOutlinedIcon />}
              onClick={downloadTemplate} disabled={downloading}>Template</Button>
            <Button size='small' variant='outlined' startIcon={<DownloadOutlinedIcon />}
              onClick={downloadMappings} disabled={downloading || !courier}
              title={courier ? `Download ${courier} mappings as Excel` : 'Select a courier first'}>
              Download Mappings
            </Button>
            <Button size='small' variant='outlined' startIcon={<UploadFileOutlinedIcon />}
              onClick={() => setUploadOpen(true)}>Upload Excel</Button>
            <Button size='small' variant='contained' startIcon={<AddOutlinedIcon />}
              onClick={openAdd}>Add Mapping</Button>
          </>
        }
      />

      {/* ── Unmapped Status Report ─────────────────────────────────────────── */}
      <Box sx={{
        borderRadius: '12px', mb: 3, overflow: 'hidden',
        border: '1px solid #E5E7EB', bgcolor: '#fff',
        boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
      }}>
        {/* Header strip */}
        <Box sx={{
          display: 'flex', alignItems: 'center', gap: 1.5,
          px: 2.5, py: 1.5, bgcolor: '#FFF7ED', borderBottom: '1px solid #FED7AA',
        }}>
          <Box sx={{
            width: 32, height: 32, borderRadius: '8px', display: 'grid', placeItems: 'center',
            bgcolor: '#EA580C', color: '#fff', flex: '0 0 auto',
          }}>
            <FileDownloadOutlinedIcon sx={{ fontSize: 18 }} />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#1F2937' }}>Unmapped Status Report</Typography>
            <Typography sx={{ fontSize: 11.5, color: '#6B7280' }}>AWBs where the courier sent a status we don't recognise — export for review or to add new mappings.</Typography>
          </Box>
        </Box>
        {/* Controls row */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, px: 2.5, py: 2, flexWrap: 'wrap' }}>
          <ToggleButtonGroup
            size='small'
            exclusive
            value={unmappedDateMode}
            onChange={(_, v) => { if (v) setUnmappedDateMode(v) }}
            sx={{
              '& .MuiToggleButton-root': { textTransform: 'none', fontWeight: 600, fontSize: 12.5, px: 2, py: 0.6 },
              '& .Mui-selected': { bgcolor: '#1565c0 !important', color: '#fff !important' },
            }}
          >
            <ToggleButton value='today'>Today</ToggleButton>
            <ToggleButton value='yesterday'>Yesterday</ToggleButton>
            <ToggleButton value='custom'>Custom</ToggleButton>
          </ToggleButtonGroup>

          {unmappedDateMode === 'custom' && (
            <TextField
              type='date'
              size='small'
              value={unmappedCustomDate}
              onChange={(e) => setUnmappedCustomDate(e.target.value)}
              inputProps={{ max: new Date().toISOString().slice(0, 10) }}
              sx={{ width: 170, '& .MuiInputBase-root': { fontSize: 13 } }}
            />
          )}

          <Box sx={{ flex: 1 }} />

          <Button
            variant='contained'
            size='small'
            startIcon={unmappedDownloading ? <CircularProgress size={14} color='inherit' /> : <FileDownloadOutlinedIcon sx={{ fontSize: 18 }} />}
            onClick={downloadUnmappedStatuses}
            disabled={unmappedDownloading || (unmappedDateMode === 'custom' && !unmappedCustomDate)}
            sx={{
              textTransform: 'none', fontWeight: 700, fontSize: 12.5, px: 2.5, py: 0.8,
              bgcolor: '#1565c0', '&:hover': { bgcolor: '#0d47a1' }, boxShadow: 'none',
              borderRadius: '8px',
            }}
          >
            {unmappedDownloading ? 'Downloading…' : 'Download Report'}
          </Button>
        </Box>
      </Box>

      {/* ── Status Mappings — separate card ───────────────────────────────── */}
      <Box sx={{
        borderRadius: '12px', overflow: 'hidden',
        border: '1px solid #E5E7EB', bgcolor: '#fff',
        boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
      }}>
        {/* Header strip */}
        <Box sx={{
          display: 'flex', alignItems: 'center', gap: 1.5,
          px: 2.5, py: 1.5, bgcolor: '#F0F7FF', borderBottom: '1px solid #BFDBFE',
        }}>
          <Box sx={{
            width: 32, height: 32, borderRadius: '8px', display: 'grid', placeItems: 'center',
            bgcolor: '#1565c0', color: '#fff', flex: '0 0 auto',
          }}>
            <EditOutlinedIcon sx={{ fontSize: 18 }} />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#1F2937' }}>Courier Status Mappings</Typography>
            <Typography sx={{ fontSize: 11.5, color: '#6B7280' }}>Select a courier to view, edit, or bulk-upload its status mappings.</Typography>
          </Box>
        </Box>

        {/* Content */}
        <Box sx={{ p: 2.5 }}>
          <Box sx={{ maxWidth: 360, mb: 2 }}>
            <Typography variant='caption' sx={{ opacity: 0.7 }}>Courier</Typography>
            <SearchSelect
              value={courier}
              onChange={(v) => { setCourier(v); setPage(0) }}
              options={couriers}
              placeholder='Select a courier…'
              loading={couriersLoading}
            />
          </Box>

          {!courier ? (
            <Alert severity='info'>Select a courier to view and manage its status mappings.</Alert>
          ) : isFetching ? (
            <TableSkeleton />
          ) : isError ? (
            <LoadErrorPanel onRetry={refetch} />
          ) : (
            <>
              <Stack direction='row' alignItems='center' sx={{ mb: 1 }}>
                <Typography variant='body2' sx={{ opacity: 0.7 }}>
                  {mappings.length} mapping{mappings.length === 1 ? '' : 's'} for {courier}
                </Typography>
                <Box sx={{ flex: 1 }} />
                {!!mappings.length && (
                  <Button size='small' variant='outlined' startIcon={<DownloadOutlinedIcon />} onClick={downloadMappings}>
                    Download Mappings
                  </Button>
                )}
              </Stack>
              {/* No fixed-height wrapper: ProzoDataGrid is autoHeight by default, so a
                  620px box made it overflow and the card's overflow:hidden clipped the
                  pagination footer off. Let it size to the page (15 rows) instead. */}
              <ProzoDataGrid
                columns={columns}
                rows={mappings}
                getRowId={(r) => r.id}
                rowCount={mappings.length}
                paginationMode='client'
                page={page}
                setPage={setPage}
                sizePerPage={sizePerPage}
                setSizePerPage={setSizePerPage}
                pagination
              />
            </>
          )}
        </Box>
      </Box>

      {/* ── Add / Edit ─────────────────────────────────────────────────────── */}
      <Modal
        open={!!form}
        onClose={() => setForm(null)}
        title={form && form.id ? 'Edit Status Mapping' : 'Add Status Mapping'}
        width={520}
        actions={[
          { label: 'Cancel', onClick: () => setForm(null) },
          { label: saving ? 'Saving…' : 'Save', primary: true, onClick: saveForm, disabled: saving, closeOnClick: false },
        ]}
      >
        {form && (
          <Stack spacing={2} sx={{ mt: 1 }}>
            <div>
              <Typography variant='caption' sx={{ opacity: 0.7 }}>Courier</Typography>
              <SearchSelect
                value={form.courier_code}
                onChange={(v) => setForm((f) => ({ ...f, courier_code: v }))}
                options={couriers}
                placeholder='Select a courier…'
                disabled={!!form.id}   // courier + status are the natural key — locked on edit
              />
            </div>
            <TextField
              label='Courier Status (raw string from the courier)'
              size='small' fullWidth
              value={form.courier_status}
              onChange={(e) => setForm((f) => ({ ...f, courier_status: e.target.value }))}
              disabled={!!form.id}
              helperText='Exact string PCIS receives in the webhook / poll, e.g. recd_at_rev_hub'
            />
            <div>
              <Typography variant='caption' sx={{ opacity: 0.7 }}>PCIS Status</Typography>
              <SearchSelect
                value={form.pcis_status_code}
                onChange={(v) => setForm((f) => ({ ...f, pcis_status_code: v }))}
                options={statusOptions}
                placeholder='Select a PCIS status…'
              />
            </div>
            <div>
              <Typography variant='caption' sx={{ opacity: 0.7 }}>Shipment Type (optional, display only)</Typography>
              <SearchSelect
                value={form.shipment_type}
                onChange={(v) => setForm((f) => ({ ...f, shipment_type: v }))}
                options={shipmentOptions}
                placeholder='— none —'
              />
            </div>
            <div>
              <Typography variant='caption' sx={{ opacity: 0.7 }}>OTP Stage (optional)</Typography>
              <SearchSelect
                value={form.otp_stage}
                onChange={(v) => setForm((f) => ({ ...f, otp_stage: v }))}
                options={OTP_OPTIONS}
                placeholder='— none —'
              />
            </div>
          </Stack>
        )}
      </Modal>

      {/* ── Delete confirm ─────────────────────────────────────────────────── */}
      <ConfirmDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        title='Delete status mapping?'
        body={deleteTarget ? `${deleteTarget.courierCode} · "${deleteTarget.courierStatus}" → ${deleteTarget.pcisStatusCode}` : ''}
        okLabel={deleting ? 'Deleting…' : 'Delete'}
        danger
        onOk={confirmDelete}
      />

      {/* ── Upload Excel ───────────────────────────────────────────────────── */}
      <Modal
        open={uploadOpen}
        onClose={closeUpload}
        title='Upload Status Mappings'
        width={640}
        actions={[
          { label: 'Close', onClick: closeUpload },
          { label: uploading ? 'Uploading…' : 'Upload', primary: true, onClick: doUpload, disabled: uploading || !uploadFile, closeOnClick: false },
        ]}
      >
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography variant='body2' sx={{ opacity: 0.8 }}>
            Download the template, fill it (courier & status are in-cell dropdowns), then upload.
            Existing (courier + status) rows are updated, new ones inserted.
          </Typography>
          <FileDrop
            accept='.xlsx'
            title='Drop the .xlsx here or click to choose'
            hint='Only the template format is accepted'
            onFile={(f) => { setUploadFile(f); setUploadResult(null) }}
            onError={(m) => toast(m, 'error')}
          />
          {uploadFile && <Typography variant='caption'>Selected: {uploadFile.name}</Typography>}

          {uploadResult && (
            <Box>
              <Stack direction='row' spacing={1} sx={{ mb: 1 }}>
                <Chip size='small' color='success' label={`Sent: ${uploadResult.sent_to_pcis ?? 0}`} />
                <Chip size='small' label={`Parsed: ${uploadResult.parsed_rows ?? 0}`} />
                {(uploadResult.validation_errors || []).length > 0 &&
                  <Chip size='small' color='error' label={`Rejected: ${uploadResult.validation_errors.length}`} />}
              </Stack>
              {(uploadResult.validation_errors || []).length > 0 && (
                <Alert severity='warning' sx={{ maxHeight: 220, overflow: 'auto' }}>
                  {uploadResult.validation_errors.map((er, i) => (
                    <div key={i}>Row {er.row}: {er.courier_code || '—'} / {er.courier_status || '—'} — {er.error}</div>
                  ))}
                </Alert>
              )}
            </Box>
          )}
        </Stack>
      </Modal>
    </>
  )
}
